/**
 * Agency workspace type + portal access module catalogs.
 */

export const AGENCY_TYPES = {
  CREDIT_REPAIR: 'credit_repair',
  FUNDGENIUS: 'fundgenius',
};

export const CREDIT_REPAIR_PORTAL_SECTIONS = [
  { key: 'dashboard', label: 'Dashboard', description: 'Main dashboard overview' },
  { key: 'credit_report', label: 'Credit Report', description: 'View and analyze credit reports' },
  { key: 'dispute_wizard', label: 'Dispute Wizard', description: 'Create and manage disputes' },
  { key: 'my_disputes', label: 'My Disputes', description: 'Track dispute status' },
  { key: 'my_documents', label: 'My Documents', description: 'Upload and view documents' },
  { key: 'credit_builder', label: 'Credit Builder', description: 'Credit improvement tools' },
  { key: 'blueprints', label: 'Blueprints', description: 'Credit repair blueprints' },
  { key: 'academy', label: 'Academy', description: 'Educational resources' },
  { key: 'settings', label: 'Settings', description: 'User settings' },
];

export const FUNDGENIUS_PORTAL_SECTIONS = [
  { key: 'dashboard', label: 'Dashboard', description: 'Main funding dashboard overview' },
  { key: 'funding_clients', label: 'Funding Clients', description: 'View and manage funding clients' },
  { key: 'client_details', label: 'Client Details', description: 'Access client identity, business, and entity profiles' },
  { key: 'applications', label: 'Applications', description: 'Track lender submissions, approvals, denials, and terms' },
  { key: 'documents', label: 'Documents', description: 'Upload and manage funding documents' },
  { key: 'lenders', label: 'Lenders', description: 'View lender strategy, products, and matching data' },
  { key: 'readiness', label: 'Readiness', description: 'Review funding readiness and profile gaps' },
  { key: 'ai_advisor', label: 'AI Advisor', description: 'Use funding strategy and application guidance' },
  { key: 'reports', label: 'Reports', description: 'View portfolio, approval, and pipeline reports' },
  { key: 'settings', label: 'Settings', description: 'FundGenius workspace settings' },
];

export const PORTAL_ACCESS_MODES = [
  { value: 'hidden', label: 'Hidden' },
  { value: 'locked', label: 'Locked' },
  { value: 'read_only', label: 'Read Only' },
  { value: 'full_access', label: 'Full Access' },
];

/** Map FundGenius HQ nav ids to portal permission keys. */
export const FUNDGENIUS_NAV_PERMISSION_MAP = {
  dashboard: 'dashboard',
  clients: 'funding_clients',
  'client-roster': 'funding_clients',
  profiles: 'client_details',
  readiness: 'readiness',
  advisor: 'ai_advisor',
  match: 'lenders',
  lenders: 'lenders',
  submissions: 'applications',
  pipeline: 'applications',
  commissions: 'reports',
  reports: 'reports',
  documents: 'documents',
  sequence: 'applications',
  settings: 'settings',
};

export function normalizeAgencyType(agencyOrType) {
  if (!agencyOrType) return AGENCY_TYPES.CREDIT_REPAIR;
  if (typeof agencyOrType === 'string') {
    const value = agencyOrType.trim().toLowerCase();
    if (
      value === AGENCY_TYPES.FUNDGENIUS
      || value === 'funding'
      || value === 'funding_broker'
      || value === 'fundgenius_hq'
    ) {
      return AGENCY_TYPES.FUNDGENIUS;
    }
    return AGENCY_TYPES.CREDIT_REPAIR;
  }

  const raw = agencyOrType.agency_type
    || agencyOrType.type
    || agencyOrType.agencyType
    || agencyOrType.platform_type
    || agencyOrType.effective_agency_type
    || agencyOrType.effective_portal_type;
  if (raw) return normalizeAgencyType(raw);

  const products = agencyOrType.enabled_products || [];
  if (products.some((p) => String(p).includes('funding'))) {
    return AGENCY_TYPES.FUNDGENIUS;
  }
  return AGENCY_TYPES.CREDIT_REPAIR;
}

export function isFundGeniusAgency(agencyOrType) {
  return normalizeAgencyType(agencyOrType) === AGENCY_TYPES.FUNDGENIUS;
}

export function getAgencyTypeLabel(agencyOrType) {
  // Prefer canonical backend classification when present.
  if (agencyOrType && typeof agencyOrType === 'object') {
    const fromClassification = agencyOrType.classification?.label;
    if (fromClassification) return fromClassification;
    const type = agencyOrType.agency_type || agencyOrType.agencyType;
    if (type === 'funding' || type === 'fundgenius' || type === 'funding_broker') {
      return 'FundGenius';
    }
    if (type === 'self_repair') return 'Self-Repair';
    if (type === 'hybrid') return 'Hybrid';
    if (type === 'internal') return 'Internal';
    if (type === 'credit_repair') return 'Credit Repair';
    // Unset / unknown: do not invent Credit Repair for display.
    if (!type) return 'Not configured';
  }
  if (typeof agencyOrType === 'string') {
    const value = agencyOrType.trim().toLowerCase();
    if (value === 'funding' || value === 'fundgenius' || value === 'funding_broker') {
      return 'FundGenius';
    }
    if (value === 'self_repair') return 'Self-Repair';
    if (value === 'hybrid') return 'Hybrid';
    if (value === 'internal') return 'Internal';
    if (value === 'credit_repair') return 'Credit Repair';
  }
  return isFundGeniusAgency(agencyOrType) ? 'FundGenius' : 'Credit Repair';
}

export function getEnterAgencyPath(agency) {
  const agencyId = agency?.agency_id || agency?.id || agency?.effective_agency_id;
  // Missing id → Agencies list for explicit "enter agency" flows only.
  // Must never be used as authenticated root / post-login default landing.
  if (!agencyId) return '/platform/agencies';
  if (isFundGeniusAgency(agency)) {
    return `/platform/fundgenius-hq/clients?agency_id=${encodeURIComponent(agencyId)}`;
  }
  // Legacy credit-repair Enter Agency lands in agency workspace (staff ops via support mode).
  return `/agency/${agencyId}/dashboard`;
}

export function getEnterAgencyPathFromSupportContext(supportContext, agencyFallback = null) {
  const redirect = supportContext?.redirect_path || supportContext?.redirect_to;
  if (redirect && String(redirect).startsWith('/')) {
    return String(redirect);
  }

  const agencyId = supportContext?.effective_agency_id
    || supportContext?.agency_id
    || agencyFallback?.agency_id
    || agencyFallback?.id;
  const portal = String(
    supportContext?.effective_portal_type
    || supportContext?.portal_type
    || ''
  ).toLowerCase();
  const agencyType = supportContext?.effective_agency_type
    || supportContext?.agency_type
    || agencyFallback?.agency_type
    || agencyFallback?.type
    || agencyFallback?.platform_type;

  if (portal === 'fundgenius_hq' || portal === 'fundgenius' || isFundGeniusAgency(agencyType)) {
    if (!agencyId) return '/platform/fundgenius-hq/clients';
    return `/platform/fundgenius-hq/clients?agency_id=${encodeURIComponent(agencyId)}`;
  }

  if (agencyId) {
    return getEnterAgencyPath({
      ...(agencyFallback || {}),
      agency_id: agencyId,
      agency_type: agencyType,
    });
  }

  return '/staff/dashboard';
}

export function getPortalSectionsForAgency(agencyOrType) {
  return isFundGeniusAgency(agencyOrType)
    ? FUNDGENIUS_PORTAL_SECTIONS
    : CREDIT_REPAIR_PORTAL_SECTIONS;
}

export function getDefaultPortalAccess(agencyOrType) {
  const sections = getPortalSectionsForAgency(agencyOrType);
  return Object.fromEntries(sections.map((s) => [s.key, 'full_access']));
}

export function getTypedPortalAccess(agency) {
  const agencyType = normalizeAgencyType(agency);
  const defaults = getDefaultPortalAccess(agencyType);
  const nested = agency?.settings?.portal_access?.[agencyType];
  if (nested && typeof nested === 'object') {
    return { ...defaults, ...nested };
  }
  if (agencyType === AGENCY_TYPES.CREDIT_REPAIR && agency?.portal_access) {
    return { ...defaults, ...agency.portal_access };
  }
  return defaults;
}

export function resolveFundGeniusModuleAccess(agency, moduleKey) {
  const access = getTypedPortalAccess(agency);
  return access[moduleKey] || 'full_access';
}

export function isFundGeniusModuleVisible(agency, moduleKey) {
  return resolveFundGeniusModuleAccess(agency, moduleKey) !== 'hidden';
}

export function canWriteFundGeniusModule(agency, moduleKey) {
  return resolveFundGeniusModuleAccess(agency, moduleKey) === 'full_access';
}

export function filterFundGeniusNavByAccess(navItems, agency) {
  if (!agency) return navItems;
  return (navItems || []).filter((item) => {
    const permissionKey = FUNDGENIUS_NAV_PERMISSION_MAP[item.id] || item.id;
    return isFundGeniusModuleVisible(agency, permissionKey);
  }).map((item) => {
    if (!item.children?.length) return item;
    return {
      ...item,
      children: item.children.filter((child) => {
        const permissionKey = FUNDGENIUS_NAV_PERMISSION_MAP[child.id] || FUNDGENIUS_NAV_PERMISSION_MAP[item.id] || child.id;
        return isFundGeniusModuleVisible(agency, permissionKey);
      }),
    };
  });
}
